import './index.css';

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';

import Select from 'components/Select';
import { ProspectOneProps, ProspectProps } from 'utils/types';
import useProspects from 'api/hooks/useProspects';
import useDatasetById from 'api/hooks/getDatasetById';

import editSvg from '/tableEdit.svg';

import ProspectMapOfflineModal from 'components/ProspectMapOfflineModal';

const ProspectMapOffline = () => {
  const [selected, setSelected] = useState<ProspectProps | null>(null);
  const [selectedProspect, setSelectedProspect] = useState<
  ProspectOneProps | null | undefined
  >(null);
  const [modal, setModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: prospects } = useProspects();
  const { data: datasetById, refetch } = useDatasetById({
    id: selected?.id,
    preferCachedData: true,
  });

  const selectOptions = useMemo(() => {
    if (prospects) {
      return prospects?.map((prospect) => ({
        value: String(prospect.id),
        label: prospect.title,
      }));
    } else {
      return [];
    }
  }, [prospects]);

  const onChangeSelect = (id: string) => {
    const filteredProspect = prospects?.find(
      (prospect) => prospect.id === Number(id),
    );

    if (filteredProspect) {
      setSelected(filteredProspect);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: '',
        cell: (cell: any) => (
          <div
            onClick={() => {
              const getSelected = datasetById?.prospects?.find(
                (prospect) => prospect?.id === cell.getValue(),
              );
              setSelectedProspect(getSelected);
              setModal(true);
            }}
            className="cursor-pointer md:w-[40px]"
          >
            <img src={editSvg} alt="edit" />
          </div>
        ),
        footer: 'id',
      },
      {
        accessorKey: 'companyName',
        header: t('companyName'),
        footer: 'companyName',
      },
      {
        accessorKey: 'status',
        header: t('status'),
        footer: 'status',
      },
      {
        accessorKey: 'rating',
        header: t('rating'),
        footer: 'rating',
      },
      {
        accessorKey: 'contact',
        header: t('contact'),
        footer: 'contact',
      },
      {
        accessorKey: 'contactNumber',
        header: t('contactNumber'),
        footer: 'contactNumber',
      },
      {
        accessorKey: 'industry',
        header: t('industry'),
        footer: 'industry',
      },
      {
        accessorKey: 'applications',
        header: t('applications'),
        footer: 'applications',
      },
      {
        accessorKey: 'latitude',
        header: t('latitude'),
        footer: 'latitude',
      },
      {
        accessorKey: 'longtitude',
        header: t('longtitude'),
        footer: 'longtitude',
      },
    ],
    [datasetById],
  );

  const data = useMemo(
    () => datasetById?.prospects ?? [],
    [datasetById?.prospects],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="relative">
      {modal && (
        <ProspectMapOfflineModal
          selectedProspect={selectedProspect}
          setModal={setModal}
          refetch={refetch}
          selectedDataSetId={selected?.id}
        />
      )}
      <div className="px-[100px] pt-[24px] md:px-[16px] md:pb-4 flex flex-col h-full">
        <div>
          <span
            onClick={() => navigate('/')}
            className="text-secondaryText cursor-pointer"
          >
            {t('home')} /
          </span>
          <span className="font-medium"> {t('prospectMap')}</span>
        </div>
        <div className="text-3xl font-bold my-4">{t('prospectMap')}</div>
        <div className="w-[358px] md:w-[345px]">
          <Select
            title={t('chooseProspect')}
            options={selectOptions}
            onChange={(el) => onChangeSelect(el?.value)}
            placeholder={t('chooseProspect')}
          />
        </div>
      </div>
      {datasetById && (
        <div className="px-[100px] pt-[24px] md:px-[16px] md:pb-4 md:overflow-scroll">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end my-4">
            <button
              className="border rounded-full p-1 flex items-center justify-center w-[50px] h-[50px] mr-2 cursor-pointer bg-primary text-white text-lg"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'<'}
            </button>
            <button
              className="border rounded-full p-1 flex items-center justify-center w-[50px] h-[50px] cursor-pointer bg-primary text-white text-lg"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {'>'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProspectMapOffline;
